import { useCallback, useMemo, useState } from "react";
import { useAuthUser } from "@/contexts/OptimizedAuthContext";
import { useRouter, useParams, useSearchParams } from "@/hooks/useRouter";
import {
  SearchChatCache,
  type CacheFilters,
  type CacheEntry,
} from "@/lib/cache/searchChatCache";

export const getInitialMode = (pathname: string): "search" | "chat" | null => {
  if (pathname?.startsWith("/search")) {
    return "search";
  }
  if (pathname?.startsWith("/chats")) {
    return "chat";
  }
  return null;
};

interface UseSearchChatCacheReturn {
  /**
   * Initial query value (from URL params or cache)
   * Use this to initialize the query state
   */
  initialQuery: string;

  /**
   * Initial filters (from URL params or cache)
   * Use this to initialize the filters state
   */
  initialFilters: CacheFilters;

  /**
   * Initial mode (forced by route or from cache)
   * Use this to initialize the mode state
   */
  initialMode: "search" | "chat";

  /**
   * Get current cached values from localStorage
   * Note: Reads fresh from localStorage and validates project IDs
   * Warning: Can cause cross-tab sync issues if used for component state initialization
   * Use initialQuery, initialFilters and initialMode instead for normal cache loading
   * Expose this for future use cases where you need to check current cache state
   */
  getCachedValues: () => CacheEntry | null;

  /**
   * Manually save to cache (bypasses debounce)
   */
  saveToCache: (storeValue: {
    mode?: "search" | "chat" | null;
    query?: string | null;
    filters?: Partial<CacheFilters>;
  }) => void;

  /**
   * Promote the new chat to the chatId
   *
   * This is used when a new chat is started but there is no chatId yet
   * once the chatId is available, the cache is promoted to the chatId
   */
  promoteNewChatToChatId: (chatId: string) => void;

  /**
   * Clear the cache
   */
  clearCache: () => void;
}

/**
 * Custom hook to manage search/chat input cache
 *
 * @param isRAGChat - Whether the chat is a RAG chat
 * @returns Cache utility functions
 */
export function useSearchChatCache(
  isRAGChat: boolean = false
): UseSearchChatCacheReturn {
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const pathname = router.pathname;
  const userId = useAuthUser();

  // This is used for the cache key. Except when using the RAG chat mode in /chats/:chatId (or other routes with :chatId)
  // it always uses 'home' as key (ie all search bars should use the same cache key)
  const cachePathname = useMemo(() => {
    if (isRAGChat && params.chatId) {
      return params.chatId;
    }
    return "home";
  }, [params, isRAGChat]);

  // Common function to get and filter cache
  const getFilteredCache = useCallback((): CacheEntry | null => {
    const cached = SearchChatCache.get(userId, cachePathname);
    if (!cached) {
      return cached;
    }

    return cached;
  }, [userId, cachePathname]);

  // Load initial cache ONCE on mount (never updates, even if localStorage changes in other tabs)
  const [initialCache] = useState<CacheEntry | null>(() => getFilteredCache());

  // Determine initial mode based on route (force mode for specific routes)
  const initialMode = useMemo((): "search" | "chat" => {
    const defaultModeFromPath = getInitialMode(pathname);
    if (defaultModeFromPath) {
      return defaultModeFromPath;
    }

    // For homepage, use cached mode if available, otherwise current mode
    return initialCache?.mode || "chat";
  }, [pathname, initialCache]);

  // Compute initial values based on route and cache
  const { initialQuery, initialFilters } = useMemo(() => {
    const queryFromUrl = searchParams.get("q") || "";
    const projectIdsFromUrl =
      searchParams.get("p")?.split(",").filter(Boolean) || [];
    const agentNamesFromUrl =
      searchParams.get("a")?.split(",").filter(Boolean) || [];
    const timeFilterFromUrl = searchParams.get("t") as
      | "today"
      | "week"
      | "month"
      | null;
    // On /search page, URL params take precedence
    if (queryFromUrl) {
      return {
        initialQuery: queryFromUrl,
        initialFilters: {
          projectIds: projectIdsFromUrl,
          branchNames: [],
          timeFilter: timeFilterFromUrl || null,
          agentNames: agentNamesFromUrl,
        },
      };
    }

    // Otherwise use cache if available
    if (initialCache) {
      return {
        initialQuery: initialCache.query,
        initialFilters: initialCache.filters,
      };
    }

    // Fallback to empty values
    return {
      initialQuery: "",
      initialFilters: {
        projectIds: [],
        branchNames: [],
        timeFilter: null,
        agentNames: [],
      },
    };
  }, [searchParams, initialCache]);

  // getCachedValues reads fresh from localStorage (for future use cases)
  // Note: Components should use initialCache to avoid cross-tab sync
  const getCachedValues = useCallback(() => {
    return getFilteredCache();
  }, [getFilteredCache]);

  const saveToCache = useCallback(
    (storeValue: {
      mode?: "search" | "chat" | null;
      query?: string | null;
      filters?: Partial<CacheFilters>;
    }) => {
      const { mode, filters = {} as Partial<CacheFilters> } = storeValue;
      const cached = getFilteredCache();
      const nextQuery =
        (Object.hasOwn(storeValue, "query")
          ? storeValue.query
          : cached?.query) || "";
      const nextFilters: Partial<CacheFilters> = {};
      nextFilters.projectIds =
        (Object.hasOwn(filters, "projectIds")
          ? filters.projectIds
          : cached?.filters?.projectIds) || [];
      nextFilters.branchNames =
        (Object.hasOwn(filters, "branchNames")
          ? filters.branchNames
          : cached?.filters?.branchNames) || [];
      nextFilters.timeFilter =
        (Object.hasOwn(filters, "timeFilter")
          ? filters.timeFilter
          : cached?.filters?.timeFilter) || null;
      nextFilters.agentNames =
        (Object.hasOwn(filters, "agentNames")
          ? filters.agentNames
          : cached?.filters?.agentNames) || [];
      SearchChatCache.set(
        userId,
        cachePathname,
        mode || cached?.mode || "chat",
        nextQuery,
        nextFilters as CacheFilters
      );
    },
    [userId, cachePathname, getFilteredCache]
  );

  const promoteNewChatToChatId = useCallback(
    (chatId: string) => {
      const cached = SearchChatCache.get(userId, "new");
      if (cached) {
        SearchChatCache.set(
          userId,
          chatId,
          cached.mode,
          cached.query,
          cached.filters
        );
        SearchChatCache.clear(userId, "new");
      }
    },
    [userId]
  );

  const clearCache = useCallback(() => {
    SearchChatCache.clear(userId, cachePathname);
  }, [userId, cachePathname]);

  return {
    initialQuery,
    initialFilters,
    initialMode,
    getCachedValues,
    saveToCache,
    clearCache,
    promoteNewChatToChatId,
  };
}
